import {
  ChangeEvent,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import { CellRendererProps, useCell } from "components/Bable"
import { css, cx } from "@emotion/css"

import { ReactComponent as CheckIcon } from "assets/check.svg"
import { ReactComponent as Deactivate } from "assets/icon-deactivate.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import GhostTextInput from "ui/inputs/GhostTextInput"
import { MarketTarget } from "models/targets"
import MoreActions from "components/MoreActions"
import { Popover } from "@material-ui/core"
import { Tooltip } from "@material-ui/core"
import WarningIcon from "@material-ui/icons/ErrorRounded"
import { formatFloatingPointNumber } from "utils/number-format"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useRow } from "components/Bable/data/useRow"

const ListCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { t } = useI18n()
  const [value] = useCell<MarketTarget>(
    rowId,
    dataKey as keyof MarketTarget,
    "",
  )
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handlePopoverOpen = (
    event: ReactMouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  return !!value ? (
    <>
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        color="primary"
        className={cx("BodyBody-2BlackRegular", styles.readonly)}
      >
        {value}
      </div>

      {open ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          PaperProps={{
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          className={styles.popover}
          classes={{ paper: styles.paper }}
        >
          <div className={styles.tooltip}>
            <span className={"BodyBody-2BlackRegular"}>{value}</span>
          </div>
        </Popover>
      ) : null}
    </>
  ) : (
    <div className={styles.alertCell}>
      <Tooltip
        title={t("admin.targets.mismatch.error")}
        placement="bottom-start"
        className={styles.errorAlert}
        classes={{ tooltip: styles.tooltip }}
      >
        <WarningIcon color="inherit" />
      </Tooltip>
    </div>
  )
}

const EditableCell = (
  props: CellRendererProps & {
    onUpdate: (row: any, label: any, value: any) => Promise<void>
  },
) => {
  const { dataKey, rowId, onUpdate } = props
  const { hasAuthority } = useAuthenticated()
  const [value, setValue, error] = useCell<MarketTarget>(
    rowId,
    dataKey as keyof MarketTarget,
    "",
  )
  const row = useRow<MarketTarget>(rowId)
  let old = useRef(value)
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }
  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      onUpdate(row.value, dataKey, value).then(() => (old.current = value))
    }
  }
  const { status } = row.value ?? {}

  return (
    <GhostTextInput
      value={value as string}
      onChange={onChange}
      onBlur={onBlur}
      className={cx(styles.input, styles.ghostInput, {
        [styles.error]: !!error,
      })}
      disabled={!hasAuthority(["AUTH_EDIT_TARGET"]) || status === "ACTIVE"}
    />
  )
}

const NumCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { t } = useI18n()
  const [value] = useCell<MarketTarget>(
    rowId,
    dataKey as keyof MarketTarget,
    "",
  )
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handlePopoverOpen = (
    event: ReactMouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  return !!value ? (
    <>
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        color="primary"
        className={cx("BodyBody-2BlackRegular", styles.readonly)}
      >
        {formatFloatingPointNumber(2)(value as number)}
      </div>

      {open ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          PaperProps={{
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          className={styles.popover}
          classes={{ paper: styles.paper }}
        >
          <div className={styles.tooltip}>
            <span className={"BodyBody-2BlackRegular"}>
              {formatFloatingPointNumber(2)(value as number)}
            </span>
          </div>
        </Popover>
      ) : null}
    </>
  ) : (
    <div className={styles.alertCell}>
      <Tooltip
        title={t("admin.targets.mismatch.error")}
        placement="bottom-start"
        className={styles.errorAlert}
        classes={{ tooltip: styles.tooltip }}
      >
        <WarningIcon color="inherit" />
      </Tooltip>
    </div>
  )
}

const StatusCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const [value] = useCell<MarketTarget>(
    rowId,
    dataKey as keyof MarketTarget,
    "",
  )
  const active = value === "ACTIVE"
  const inactive = value === "INACTIVE"
  const draft = value === "DRAFT"

  return (
    <div
      className={cx(
        "BodyBody-xsWhiteRegular",
        { [styles.draft]: draft },
        { [styles.active]: active },
        { [styles.inactive]: inactive },
      )}
    >
      {value}
    </div>
  )
}

const styles = {
  readonly: css`
    margin: var(--spacing-m);
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  alertCell: css`
    display: flex;
    padding: var(--spacing-s);
    align-items: center;
    width: 100%;
    height: 100%;
    background-color: var(--tertiary-lighter-yellow);
  `,
  errorAlert: css`
    color: var(--tertiary-light-yellow);
  `,
  tooltip: css`
    background-color: var(--tertiary-light-yellow);
  `,
  draft: css`
    margin: var(--spacing-m);
    background-color: var(--grey);
    padding: 2px var(--spacing-xs);
  `,
  active: css`
    margin: var(--spacing-m);
    background-color: var(--primary-main);
    padding: 2px var(--spacing-xs);
  `,
  inactive: css`
    margin: var(--spacing-m);
    background-color: var(--red);
    padding: 2px var(--spacing-xs);
  `,
  input: css`
    font-size: 13px;
    :hover {
      cursor: pointer;
    }
    .MuiOutlinedInput-input {
      font-size: 13px;
    }
  `,
  ghostInput: css`
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  paper: css`
    pointer-events: auto;
  `,
  popover: css`
    margin-top: var(--spacing-xxs);
    pointer-events: none;
  `,
  numeric: css`
    text-align: right;
  `,
}

const commonColumns = (
  onUpdate: (row: MarketTarget, label: string, value: string) => Promise<void>,
) => {
  return [
    {
      id: "1",
      Header: "target for tv",
      dataKey: "name",
      Cell: ListCell,
    },
    {
      id: "2",
      Header: "universe",
      dataKey: "universe",
      Cell: NumCell,
    },
    {
      id: "3",
      Header: "external code",
      dataKey: "externalCode",
      Cell: (props: CellRendererProps) => {
        return <EditableCell onUpdate={onUpdate} {...props} />
      },
    },
    {
      id: "4",
      Header: "status",
      dataKey: "status",
      Cell: StatusCell,
    },
  ]
}

const actionColumn = (
  onActivate: (row: MarketTarget) => void,
  onDeactivate: (row: MarketTarget) => void,
  onDelete: (row: MarketTarget) => void,
) => {
  return [
    {
      id: "5",
      Header: "actions",
      dataKey: "",
      width: 80,
      Cell: function (props: CellRendererProps) {
        const { t } = useI18n()
        const { rowId } = props
        const row = useRow<MarketTarget>(rowId)
        const { status } = row.value ?? {}

        return (
          <MoreActions
            options={[
              //ACTIVATE
              {
                label: t("activate"),
                icon: <CheckIcon />,
                hidden: status === "ACTIVE",
                onClick: () => onActivate(row.value),
              },
              //DEACTIVATE
              {
                label: t("deactivate"),
                icon: <Deactivate />,
                hidden: status !== "ACTIVE",
                onClick: () => onDeactivate(row.value),
              },
              // DELETE
              {
                label: t("actions.delete"),
                icon: <DeleteIcon />,
                hidden: status !== "DRAFT",
                onClick: () => onDelete(row.value),
              },
            ]}
          />
        )
      },
    },
  ]
}

export const columns = (
  onActivate: (row: MarketTarget) => void,
  onDeactivate: (row: MarketTarget) => void,
  onDelete: (row: MarketTarget) => void,
  onUpdate: (row: MarketTarget, label: string, value: string) => Promise<void>,
  actionAllowed: boolean,
) => {
  return actionAllowed
    ? [
        ...commonColumns(onUpdate),
        ...actionColumn(onActivate, onDeactivate, onDelete),
      ]
    : commonColumns(onUpdate)
}
